import type { ComponentType } from "react";
import { Product } from "@/api/product-api";
import { getLocalizedText } from "@/components/tactsuit/components/ProductSections";
import { ProductUtil } from "@/utils/ProductUtil";
import { ExoshockProductInformation } from "@/components/tactsuit/components/buy-tactsuit/exoshock-bundle-sections";

/**
 * 어드민 Product Sections 대신 **시안 문구를 그대로 그리는** 제품(slug) — 번들 전용 PDP 가 자기 문구를 갖는 경우.
 * 여기 있으면 구성품 섹션 계산을 건너뛴다. 게이트(`hasProductInformation`)도 이 표를 같이 본다.
 */
const PRODUCT_INFORMATION_OVERRIDE: Partial<Record<string, ComponentType>> = {
  "exoshock-bundle": ExoshockProductInformation,
};

/** 아코디언을 띄울지 — 내용 렌더와 같은 판정(재정의 표 → 어드민 블록) */
export const hasProductInformation = (
  products: Product[],
  displayProduct: Product,
  locale: string,
) =>
  !!PRODUCT_INFORMATION_OVERRIDE[displayProduct.slug] ||
  getProductInformationBlocks(products, displayProduct, locale).length > 0;

/**
 * Product Information 블록 계산.
 *
 * 위 "Upgrade your device" 선택 상태(displayProduct)에 따라 갱신된다.
 * - 단일 제품: 해당 제품의 스펙 불릿
 * - 번들: 구성 제품별로 제품명 + 스펙 불릿 (피그마 Product Information 구조)
 *
 * 컴포넌트와 "아코디언을 띄울지" 게이트가 **같은 함수를 쓰도록** 밖으로 뺐다.
 * 게이트에서 조건을 다시 구현하면 둘이 갈린다(이 PR 에서 반복 발생한 유형).
 */
export function getProductInformationBlocks(
  products: Product[],
  displayProduct: Product,
  locale: string,
) {
  const toBlock = (p: Product) => ({
    slug: p.slug,
    name: p.name,
    items: (p.productSections ?? [])
      .flatMap((section) =>
        section.items.map((item) => getLocalizedText(item, locale).trim()),
      )
      .filter((text) => text.length > 0),
  });

  const components = ProductUtil.getBundleComponents(products, displayProduct);

  // ⚠️ 컴포넌트 개수가 아니라 **스펙이 실제로 있는지**로 폴백을 판단한다.
  // DK3 는 `getBundleComponents` 가 자기 자신을 제외하고 사이즈 세트 번들
  // (`tactglove-dk3-bundle`) 만 돌려주는데 그 번들엔 productSections 가 0개다.
  // 개수로만 보면(components.length === 0) 폴백이 걸리지 않아 DK3 자신의 스펙
  // ("3.5 hrs playtime" 등)이 있는데도 Product Information 이 통째로 비었다.
  let blocks = components.map(toBlock).filter((b) => b.items.length > 0);
  if (blocks.length === 0) {
    blocks = [toBlock(displayProduct)].filter((b) => b.items.length > 0);
  }

  return blocks;
}

export function ProductInformationContent({
  products,
  displayProduct,
  locale,
}: {
  products: Product[];
  displayProduct: Product;
  locale: string;
}) {
  const Override = PRODUCT_INFORMATION_OVERRIDE[displayProduct.slug];
  if (Override) return <Override />;

  const blocks = getProductInformationBlocks(products, displayProduct, locale);

  if (blocks.length === 0) {
    return null;
  }

  return (
    // 블록(구성 제품) 간 20px. 피그마(28715:139651)는 단일 블록만 그려져 있어
    // 다중 블록 간격은 기존 값을 유지한다.
    <div className="flex flex-col gap-5">
      {blocks.map((block) => (
        // 피그마 28715:139655 — 제품명 → 목록 12px
        <div key={block.slug} className="flex flex-col gap-3">
          {/* 제품명은 블록이 하나여도 표시한다(피그마가 단일 제품 페이지에서도
              "TactSuit Pro" 를 그린다). 번들일 때 구성 제품별 스펙과 형태가
              같아져서 분기도 사라진다. Title/Medium 16/22 w500 ls.15 */}
          <h4 className="text-lg font-500 tracking-[0.15px] text-coolgray-90">
            {block.name}
          </h4>
          {/* Body/Medium 14/18 ls.25 — `text-base` 가 이미 14/18 이라
              leading 을 덧대면 오히려 어긋난다(기존 leading-relaxed 제거) */}
          <ul className="list-outside list-disc pl-5 text-base tracking-[0.25px] text-coolgray-90">
            {block.items.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}
